/* eslint-disable import/prefer-default-export */
import React, {
    memo
} from 'react';

const postsDescription = `The number of posts containing mentions of your search terms for the selected time period. A single post can contain multiple mentions of your search terms.`;
const postsLast24HoursDescription = `The number of posts containing mentions of your search terms for the last 24 hours. A single post can contain multiple mentions of your search terms.`;
const sentimentDescription = `A post sentiment may be positive, negative, neutral or mixed. Mixed indicates there are both positive and negative sentiments within the post.`;
const impressionsDescription = `The estimated number of people who might have viewed posts around your search terms over time. This metric is not available for all sources.`;
const authorsDescription = `The number of people who have posted mentions of your search terms during the selected time period.`;
const netSentimentDescription = `A ratio of positive to negative opinions ranging from +100 to -100, for the selected time period.`;
const netSentimentLast24HoursDescription = `A ratio of positive to negative opinions ranging from +100 to -100, for the the last 24 hours.`;
const publishedAtDescription = `The Published At is approximately the day the content was published on the listed source. Due to some latency in indexing content the day may vary slightly from actual published day.`;
const shareOfVoiceDescription = `The percentage of posts a saved search has compared to the other searches in this list.`;

const {maxTopicsPerLandscape} = RivalIQ.enums.listeningQueryTypes.QuidTopic;

const SearchTipMatchKeywordVariations = () => (
    <>
        Automatically search variations like plurals and common misspellings.<br/>
        For example: <Pill term='run'/> matches runs and running.
    </>
);

const SearchTipFindExactKeywords = () => (
    <>
        Use quotes to limit results to the exact keyword.<br/>
        For example: <Pill term='“Shop”'/> only matches shop, not shopping, or shopped.
    </>
);

const SearchTipMatchVariationsOfAKeyword = () => (
    <>
        Use an asterisk (*) at the end of a keyword or after a hashtag to match any number of characters.<br/>
        For example: <Pill term='#*RivalIQ*'/> will match #loveRivalIQ, and #rivaliqanalytics.
    </>
);

const ViewMoreSearchTips = () => (
    <a
        href='https://help.rivaliq.com/en/articles/8019160-best-practices-for-building-listening-topics'
        target='_blank'
        rel='noopener noreferrer'
    >
            View more search tips&#160;
        <span
            className='fa fa-external-link fa-fw'
        />
    </a>
);

export const listeningDefinitions = {
    'metrics': {
        count: postsDescription,
        countLast24Hours: postsLast24HoursDescription,
        sentimentDescription,
        impressions: impressionsDescription,
        authors: authorsDescription,
        netSentiment: netSentimentDescription,
        netSentimentLast24Hours: netSentimentLast24HoursDescription,
        shareOfVoice: shareOfVoiceDescription
    },
    'shell': {
        timePeriodChange: (timePeriod) => `Your time period has been changed to ${timePeriod.displayString}.`
    },
    'glance-widget': {
        title: 'At a Glance',
        description: 'This panel displays four key metrics for your search terms. The sparkline represents the change over time.'
    },
    'positive-negative': {
        title: 'Positive and Negative Posts',
        description: 'This panel displays the number of posts with positive or negative sentiment',
        footer: 'Neutral posts are not included in this panel.',
        positivePosts: `The number of posts matching your primary terms expressing positive sentiment toward your primary search terms, such as “I really like Pepsi because it tastes less sugary.”`,
        negativePosts: `The number of posts matching your primary terms expressing negative sentiment toward your search terms, such as “Delta is a terrible airline.”`
    },
    'sentiment-drivers': {
        title: 'Sentiment Drivers by Type',
        description: <SentimentDriverDescription/>
    },
    'activity-sentiment': {
        title: 'Posts and Sentiment',
        description: `This panel helps you visualize the number of conversations happening about your search term over time and identify any correlation between volume and sentiment. `,
        activityTitle: `Posts`,
        activityDescription: `This line chart helps you visualize the number of conversations taking place about your search terms over time.`,
        netSentimentTitle: `Net Sentiment`,
        netSentimentDescription: `This line chart helps you visualize how the sentiment of your search terms changed over time. Most brands have a net sentiment score of 50. `

    },
    'phrase-analysis': {
        title: 'Popular Terms',
        description: 'This panel pulls out the most common words, phrases or emojis used in posts that match your search terms.'
    },
    'author-demographics': {
        title: 'Author Demographics',
        description: `This panel shows you the number of posts by the author's location and gender. The demographic data is not available for all authors. We may have location for an author but not gender or vice versa.`,
        footer: 'The author gender and location data is not available for all posts.'
    },
    'author-analysis': {
        title: 'Authors',
        description: `This panel shows you which authors are posting most frequently about your search terms. When available, the name displayed is either the author's handle or username. `
    },
    'domain-analysis': {
        title: 'Domains',
        description: `This panel shows you the websites that have the most posts about your search terms.`
    },
    'hashtag-analysis': {
        title: 'Hashtags',
        description: `The most common hashtags used in posts that matched your search terms.`
    },
    'brand-analysis': {
        title: 'Brands Mentioned',
        description: `The most common brands used in posts that matched your search terms.`
    },
    'person-analysis': {
        title: 'People Mentioned',
        description: `The most common people used in posts that matched your search terms.`
    },
    'posts-widget': {
        title: 'Sample Posts',
        description: `A sample of posts relevant to your query.`,
        sentimentDescription,
        impressionsDescription,
        postsDescription,
        publishedAtDescription
    },
    'geo-widget': {
        title: 'breakdown geo-widget copy tbd',
        description: 'number of posts matching this author text tbd'
    },
    'sample-topics': {
        title: 'Sample Searches'
    },
    'account-listening': {
        title: (type) => (`All ${type} in Your Account`),
        topicOrThemeUnavailable: 'Topic/Theme no longer available.'
    },
    'account-listening-summary': {
        title: `Summary`,
        Description: ({savedSearchesUsed, planSavedSearchesLimit, numberOfSavedSearchesLandscapes, onContactUsClick, hasCompanionMode, numberOfQuidTopics, numberOfQuidTopicsLandscapes}) => (
            <ul>
                <li>
                You are currently tracking <b>{savedSearchesUsed} of {planSavedSearchesLimit} saved searches</b> across <b>{numberOfSavedSearchesLandscapes} landscape{numberOfSavedSearchesLandscapes !== 1 ? 's' : ''}</b>.&nbsp;
                    <button
                        onClick={onContactUsClick}
                        className='tlw-flex-grow tlw-whitespace-nowrap tlw-border-none tlw-bg-transparent tlw-p-0 tlw-text-left tlw-text-blue-400 hover_tlw-text-blue-450 hover_tlw-underline focus_tlw-text-blue-450 focus_tlw-underline'
                    >
                    Contact us to add {planSavedSearchesLimit > 0 ? 'more saved searches' : 'Social Listening Saved Seaches'} to your account.
                    </button>
                </li>
                {hasCompanionMode && <li>
                You are currently tracking <b>{numberOfQuidTopics} Quid Monitor Topics</b> across <b>{numberOfQuidTopicsLandscapes} landscape{numberOfQuidTopicsLandscapes !== 1 ? 's' : ''}</b>.&nbsp;
                </li>}
            </ul>
        )
    },
    'quid-topics': {
        title: `Connected Quid Monitor Topics`,
        topicMetaInfo: (topicDescription, themeNames, topicTimeInterval, topicLanguage, topicSentimentFocus) => (
            <div>
                <div className='tlw-mb-6'>
                    {topicDescription}
                </div>
                <div>
                    <b>Themes:</b> {!themeNames ? 'N/A' : themeNames}
                </div>
                <div>
                    <b>Dates:</b> {topicTimeInterval}
                </div>
                <div>
                    <b>Language:</b> {topicLanguage ?? 'N/A'}
                </div>
                <div>
                    <b>Sentiment Focus:</b> {topicSentimentFocus}
                </div>
            </div>
        ),
        topicOrThemeUnavailable: 'Topic/Theme no longer available.',
        topicOrThemeUnavailableInfoBubble: 'The Topic and/or Theme is no longer available.'
    },
    'configure-topic-modal': {
        title: 'Configure Quid Monitor Topic',
        description: 'Select a Quid Monitor Topic configuration to add to Rival IQ.',
        disabledTooltipText: `Only ${maxTopicsPerLandscape} topics are allowed per landscape. Use another landscape to view additional topics.`,
        topicDescription: `These topics come from your Quid Monitor account. Select a topic to connect to Quid Compete and you will be able to view key insights from each topic. You can view and interact with the topic from this platform, but the topic can only be edited from within Quid Monitor.`,
        themeDescription: `Adding a theme allows you to narrow a topic to only include information from the selected theme. If you apply two themes the topic results must match both the first and the second theme. You can select up to 5 themes to apply to a topic. `,
        topicMetaInfo: (description, owner, timeInterval, statusStandby) => (
            <div>
                <div className='tlw-mb-6'>
                    {description}
                </div>
                <div>
                    <b>Owner:</b> {owner}
                </div>
                <div>
                    <b>Dates:</b> {timeInterval}
                </div>
                {statusStandby &&
                    <div>
                        <b>Status:</b> Standby
                    </div>
                }
            </div>
        ),
        themeMetaInfo: (owner) => (
            <div>
                <b>Owner:</b> {owner}
            </div>
        )
    },
    'saved-search': {
        associatedTermsDescription: `These are the top terms used in documents that matched your search settings. Hover over a term to include or exclude the term from your results.`,
        availableInQuid: <AvailableInQuidDescription/>,
        chooseSourceTitle: `Choose Sources`,
        chooseSourceDescription: (hasCompanionMode) => <ChooseSourceDescription hasCompanionMode={hasCompanionMode}/>,
        defineQueryTitle: `Choose Keywords`,
        defineQueryDescription: `Select keywords and refine your search using the filters below.`,
        defineQuerySubtitle: `Enter key terms and phrases that you want to analyze. When monitoring a brand include all brand name variations including branded hashtags in the primary terms. Use the include terms to narrow your search to your industry or category if needed.`,
        excludeAuthorsTitle: `EXCLUDE authors`,
        excludeAuthorsDescription: <ExcludeAuthorsDescription/>,
        excludeAuthorsFilterLabel: `high-volume authors`,
        excludeDomainsTitle: `EXCLUDE domains`,
        excludeDomainsDescription: `Narrow results by excluding the website of origin. This will also exclude subdomains of the website.`,
        excludeDomainsFilterLabel: `high-volume domains`,
        includedTermsTitle: `Include Terms`,
        languageTitle: `Language`,
        languageDescription: `Change the language of your results or select "all" to return results in multiple languages`,
        noiseSuppressionTitle: `Reduce Noise`,
        noiseSuppressionDescription: `Use the additional options to exclude results based on content type, author, or domain.`,
        previewTopicTitle: `Preview Search`,
        primaryTermsTooltipTitle: 'Primary Terms',
        primaryTermsTitle: `FIND posts with any of these primary terms`,
        primaryTermsDescription: <PrimaryTermsDescription/>,
        primaryTermsOptionalIncludeTitle: `Include Terms`,
        primaryTermsOptionalIncludePrompt: `INCLUDE posts with any of the following`,
        primaryTermsOptionalIncludeDescription: <PrimaryTermsOptionalIncludeDescription/>,
        primaryTermsOptionalExcludeTitle: `Exclude Terms`,
        primaryTermsOptionalExcludeDescription: <PrimaryTermsOptionalExcludeDescription/>,
        primaryTermsOptionalExcludePrompt: `EXCLUDE posts with any of the following`,
        savedSearchTitle: `saved search title tbd`,
        savedSearchDescription: `saved search description tbd`,
        sourceTitle: `Source`,
        sourceDescription: `Source copy tbd`
    },
    'saved-search-list': {
        title: 'My Saved Searches',
        description: <SavedSearchListDiscription/>
    },
    'search-recommendations': {
        description: `AI-generated key terms and queries based on companies in your landscape.`,
        // this is from the recommendations data source. if it changes here, it should change there too
        NUM_RECOMMENDATIONS_TO_GENERATE: 3,
        title: 'Recommendations to get started'
    },
    'topics-upsell': {
        title: 'Upgrade now to save, monitor, and compare your saved searches.',
        titleTrial: 'Save, monitor, and compare your saved searches with Engage or Engage Pro.',
        description: <UpsellDescription/>
    },
    'create-saved-search-button': {
        upsellAction: <UpsellAction/>,
        trialUpsellAction: <TrialUpsellAction/>,
        outOfTopics: (num) => `You have used ${num} of ${num} saved searches in your plan. Please contact your customer success manager to add additional searches.`,
        topicsUnsupported: `Saved Searches are not available in your plan.`,
        saveTopic: `Save Search`,
        saveAsTopic: `Save Search`,
        saveAsNewTopic: `Duplicate Search`,
        updateTopic: `Save Changes`,
        updateSampleTopicTooltip: `Sample searches cannot be edited. Please save as a new search if you wish to view any changes.`
    },
    'source-chart': {
        title: 'Sources',
        description: 'The panel shows you where conversations are taking place by website category. Twitter is labeled as a specific source type.'
    },
    'save-as-saved-search': {
        title: <SaveTopicHeader/>,
        footer: (used, allowed) => `You have used ${used} of ${allowed} saved searches your plan allows.`,
        emptyNameError: 'Name cannot be empty.',
        nameTooLongError: (maxNameLength) => `Must be under ${maxNameLength} characters.`,
        nameNotUniqueError: 'Name must be unique.'
    },
    'toolbar-dropdown': {
        availableInQuid: <AvailableInQuidDescription/>,
        disabledTooltip: (type, dropdown) => `This filter is limited in the ${type} settings. Please edit the ${type} to update the ${dropdown} availability.`,
        sentimentsFromTheme: 'Sentiment follows the theme settings.'
    },
    'instant-search': {
        primaryTermsPlaceholder: `Enter one or more terms to define your search`,
        sourceInfoBubble: `Instant Search matches your terms with documents from millions of domains.`
    },
    'instant-search-tips': {
        title: 'Here are some other ideas for using Instant Search!',
        href: 'https://help.rivaliq.com/en/articles/8025863-how-to-incorporate-social-listening-into-your-marketing-strategy'
    },
    'search-tips': {
        title: 'Search Tips',
        tipsListTips: [
            {
                summary: 'Search any case',
                detail: 'Searches are not case sensitive.'
            },
            {
                summary: 'Match keyword variations',
                detail: <SearchTipMatchKeywordVariations/>
            },
            {
                summary: 'Find exact keywords',
                detail: <SearchTipFindExactKeywords/>
            },
            {
                summary: 'Use wildcards',
                detail: <SearchTipMatchVariationsOfAKeyword/>
            },
            {
                detail: <ViewMoreSearchTips/>
            }
        ]
    },
    'theme-inclusions': {
        themesNotAvailable: 'Noise reduction filters are only avaliable for English results.'
    },
    'use-content-modal': {
        subTitle: 'A sample of relevant posts:'
    },
    'quid-monitor-topic-configure-modal': {
        duplicateComboError: 'Select a unique Topic/Theme combination not already in this landscape to save'
    },
    'powered-by-quid': {
        url: 'https://www.quid.com/?utm_source=rivaliq.com&utm_medium=referral&utm_campaign=socla',
        text: 'Discover impactful consumer and market insights.'
    },
    listeningQueryTypes: RivalIQ.enums.listeningQueryTypes
};

function ChooseSourceDescription({
    hasCompanionMode
}) {

    const RivalIQSources = RivalIQ.metrics.new.enums.ListeningSources.RivalIQSources;
    const orderedRivalIQSourceKeys = _.without(_.keys(RivalIQSources), RivalIQSources.Other.id)
        .sort()
        .concat([RivalIQSources.Other.id]);

    const QuidSources = RivalIQ.metrics.new.enums.ListeningSources.QuidSources;
    const orderedQuidSourceKeys = _.without(_.keys(QuidSources), QuidSources.Other.id)
        .sort()
        .concat([QuidSources.Other.id]);

    const sources = hasCompanionMode ? QuidSources : RivalIQSources;
    const sourceKeys = hasCompanionMode ? orderedQuidSourceKeys : orderedRivalIQSourceKeys;

    const listItems = _.map(sourceKeys, (source) => {
        return (
            <li key={source}
                className='tlw-py-1'>
                <strong>{sources[source].name}: </strong>
                {sources[source].description}
            </li>
        );
    });

    return (
        <div>
            Narrow results to include posts found on specific sites such as:
            <ul className='tlw-pl-7.5 tlw-pt-5'>{listItems}</ul>
        </div>
    );
}

const Pill = memo(function Pill({className, term}) {
    return (
        <span className={`tlw-rounded-[15px] tlw-bg-gray-150 tlw-px-5 tlw-py-1 tlw-text-black ${className ?? ''}`}>{term}</span>
    );
});

const primaryTermsExamples = ['Rival IQ', '@rival_iq', '#rivaliq'];
function PrimaryTermsDescription() {
    const pills = _.map(primaryTermsExamples, (term) => {
        return <Pill
            className='tlw-mr-2.5'
            key={term}
            term={term}/>;
    });

    return (
        <>
            <div>
                Enter at least one keyword to search, typically a brand, product, or person. To broaden your search, enter common variations, handles, and hashtags.
            </div>
            <div className='tlw-pb-3 tlw-pt-5'>Example: {pills}</div>
        </>
    );
}

function PrimaryTermsOptionalIncludeDescription() {
    return (
        <div>
            Optionally narrow your results by adding words related to your industry or category.<br/><br/>
            Be careful when adding include terms. You might be tempted, for example, to specify <Pill term='detergent'/> as an include term for the Tide brand, but this is only useful if you want to exclude all sound bites that contain <Pill term='Tide'/> <em>without</em> <Pill term='detergent'/>.
        </div>
    );
}

function PrimaryTermsOptionalExcludeDescription() {
    return (
        <div>
            Exclude specific terms if your results are too broad or unrelated to your intended search.<br/><br/>
            If your brand name is ambiguous, we recommend including words related to your industry or category. For example, the brand <Pill term='Tide'/> may need to exclude words such as <Pill term='ocean'/> or <Pill term='Alabama'/>.
        </div>
    );
}

function SentimentDriverDescription() {
    return (<div>
        The sentiment drivers show you what is motivating positive and negative sentiment around your search terms. The size of the term in the word cloud indicates the number of matching posts relative to other terms in the cloud.

        <ul>
            <li><b>Attributes:</b> Words, phrases, or emojis that are generating the most positive and negative sentiment around your search terms.</li>
            <li><b>Behaviors:</b> Positive behaviors (“buy”) and negative behaviors (“avoid”) that are being expressed most frequently in relation to your search terms.</li>
            <li><b>Emotions:</b> Positive emotions (“love”) and negative emotions (“hate”) that are being expressed most frequently in relation to your search terms.</li>
        </ul>
    </div>);
}

function UpsellAction() {
    return (<div>
        Available in Engage Pro. <a href='/manage#subscription'>Compare Plans</a>
    </div>);
}

function TrialUpsellAction() {
    return (<div>
        <a
            href='https://calendly.com/markpearson'
            target='_blank'
            rel='noreferrer'>
            Schedule a demo
        </a> to unlock features.
    </div>);
}

function UpsellDescription() {
    return (
        <ul className='tlw-my-7 tlw-list-outside'>
            <li>Analyze brand perceptions and key sentiment drivers</li>
            <li>Spot conversation spikes with always-on alerting</li>
            <li>Identify online advocates and top sources for conversations about your brand</li>
        </ul>
    );
}

function ExcludeAuthorsDescription() {
    return (
        <div>
            Narrow results by excluding specific authors or handles.
        </div>
    );
}

function SaveTopicHeader() {
    return (
        <div>
            <h4 className='tlw-m-0 tlw-text-lg tlw-leading-base tlw-text-gray-900'>Name Search</h4>
            <div className='tlw-text-md'>These will be accessed via Saved Searches in navigation.</div>
        </div>
    );
}

function SavedSearchListDiscription() {
    return (
        <span>
            Please note: Saved Searches may be in other landscapes in your account. <a href='/account/listening'>View all saved searches.</a>
        </span>
    );
}

function AvailableInQuidDescription() {
    return (
        <span>
            Quid delivers AI-powered consumer and market intelligence to enable business reinvention in a noisy and unpredictable world.&nbsp;
            <a
                href={listeningDefinitions['powered-by-quid'].url}
                target='_blank'
                rel='noreferrer'>
                Learn more.
            </a>
        </span>
    );
}
